import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const bgImages = [
  "https://public.readdy.ai/ai/img_res/c0626c977c66c086c2a2193ec4074351.jpg",
  "https://readdy.ai/api/search-image?query=cinematic%20film%20camera%20close%20up%20anamorphic%20lens%20bokeh%20dark%20production%20set%20dramatic%20golden%20rim%20light%20professional%20cinematography%20equipment%20very%20dark%20near%20black%20warm%20glow%20premium%20luxury%20filmmaking%20atmosphere%20behind%20scenes&width=1920&height=1080&seq=hero_v3_002&orientation=landscape",
  "https://readdy.ai/api/search-image?query=professional%20video%20production%20director%20monitor%20playback%20behind%20scenes%20dark%20studio%20dramatic%20lighting%20broadcast%20commercial%20production%20team%20working%20moody%20cinematic%20atmosphere%20rich%20deep%20shadows%20golden%20warm%20light&width=1920&height=1080&seq=hero_v3_003&orientation=landscape",
  "https://readdy.ai/api/search-image?query=massive%20professional%20lighting%20rig%20overhead%20broadcast%20studio%20dark%20dramatic%20beam%20shafts%20of%20light%20spotlights%20stage%20production%20equipment%20dark%20industrial%20cinematic%20atmosphere%20premium%20production%20luxury%20high%20contrast&width=1920&height=1080&seq=hero_v3_004&orientation=landscape",
];

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentImg, setCurrentImg] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImg((prev) => (prev + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-start overflow-hidden bg-[#020202]">
      {/* Crossfading cinematic backgrounds */}
      {bgImages.map((src, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
          style={{ opacity: currentImg === i ? 1 : 0 }}
        >
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ transform: `scale(1.06) translateY(${scrollY * 0.2}px)` }}
          >
            <img
              src={src}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      ))}

      {/* Multi-layer depth overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#020202]/90 via-[#020202]/50 to-[#020202]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020202] via-transparent to-[#020202]/60" />
      <div className="absolute inset-0 bg-grain opacity-35 pointer-events-none" />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 50%, transparent 30%, rgba(2,2,2,0.5) 100%)' }} />

      {/* Vertical accent lines */}
      <div className="absolute left-8 lg:left-14 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/20 to-transparent" />

      {/* Slide indicators */}
      <div className="absolute right-8 lg:right-14 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20">
        {bgImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentImg(i)}
            className={`cursor-pointer transition-all duration-500 ${currentImg === i ? 'bg-gold h-10 w-0.5' : 'bg-white/20 h-4 w-0.5 hover:bg-white/40'}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Main content */}
      <div
        className={`relative z-10 w-full pt-40 pb-32 transition-all duration-1200 ease-out ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 lg:px-20">
          {/* Eyebrow */}
          <div className={`flex items-center gap-5 mb-12 transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="w-16 h-px bg-gold" />
            <span className="font-sans text-gold text-[0.62rem] tracking-[0.4em] uppercase font-medium">
              Central Florida &nbsp;&bull;&nbsp; Est. 2005 &nbsp;&bull;&nbsp; Broadcast-Level Production
            </span>
          </div>

          {/* Headline */}
          <div className={`mb-10 transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1
              className="font-serif font-bold text-white leading-[0.95] mb-0"
              style={{ fontSize: 'clamp(3.8rem, 9vw, 8.5rem)', letterSpacing: '-0.03em' }}
            >
              Cinematic
              <br />
              Production
              <br />
              <span className="relative inline-block">
                That{' '}
                <em className="not-italic text-gold">Commands</em>
              </span>
              <br />
              <span className="text-white/25">Attention.</span>
            </h1>
          </div>

          {/* Sub + CTA row */}
          <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-12 max-w-5xl transition-all duration-1000 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="max-w-lg">
              <div className="w-10 h-px bg-gold/60 mb-6" />
              <p className="font-sans text-white/65 font-light leading-[1.85]" style={{ fontSize: '1.05rem' }}>
                20+ years delivering broadcast-grade video production for the world's most recognized brands and organizations. From commercial campaigns to live broadcast — we execute at the highest standard.
              </p>
            </div>
            <div className="flex flex-col gap-3.5 flex-shrink-0">
              <Link
                to="/contact"
                className="whitespace-nowrap group font-sans text-[0.68rem] font-semibold tracking-[0.2em] uppercase px-10 py-5 bg-gold text-[#020202] hover:bg-white transition-all duration-300 cursor-pointer inline-flex items-center gap-3"
              >
                Request Consultation
                <i className="ri-arrow-right-line group-hover:translate-x-1.5 transition-transform duration-300" />
              </Link>
              <Link
                to="/services"
                className="whitespace-nowrap font-sans text-[0.68rem] font-medium tracking-[0.2em] uppercase px-10 py-5 border border-white/25 text-white hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer inline-flex items-center gap-3"
              >
                <i className="ri-play-circle-line" />
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom credential bar */}
      <div className={`absolute bottom-0 left-0 right-0 bg-[#020202]/80 backdrop-blur-md border-t border-white/5 transition-all duration-1000 delay-800 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-8 lg:px-20 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            {[["McDonald's","National Brand"],["Disney","Entertainment"],["ABC Network","Broadcast"],["FOX News","Media"],["HP","Technology"]].map(([name, tag]) => (
              <div key={name} className="hidden md:flex flex-col">
                <span className="font-serif text-xs font-bold text-white/45 tracking-wide">{name}</span>
                <span className="font-sans text-[0.5rem] tracking-[0.25em] uppercase text-gold/35">{tag}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="font-sans text-[0.6rem] tracking-[0.2em] uppercase text-gold/60 whitespace-nowrap">20+ Years of Broadcast Excellence</span>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className={`absolute bottom-16 left-8 lg:left-14 flex items-center gap-3 transition-all duration-1000 delay-900 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex flex-col gap-1">
          <div className="w-px h-8 bg-gold/60 mx-auto" />
          <div className="w-px h-4 bg-gold/30 mx-auto" />
        </div>
        <span className="font-sans text-[0.58rem] tracking-[0.3em] uppercase text-white/30">Scroll</span>
      </div>
    </section>
  );
}
